import { Store } from 'src/store';
import getPrice from 'src/store/price/selector/getPrice';
import getCurrencyId from 'src/store/price/selector/getCurrencyId';
import getPriceFormat from 'src/store/price/selector/getPriceFormat';
import getProductTypeId from 'src/store/design/selector/getProductTypeId';
import getTotalPagesCount from 'src/store/design/selector/getTotalPagesCount';
import getMinPagesCount from 'src/store/design/selector/getMinPagesCount';
import getUsedClipArtsPrice from 'src/store/addon/selector/getUsedClipArtsPrice';
import getUpsellTotalPrice from 'src/store/upsell/selector/getUpsellTotalPrice';
import getProductBundleSelectedProductsTotalPrice from 'src/store/productBundle/selector/getProductBundleSelectedProductsTotalPrice';
import isFeatureEnabled from 'src/store/app/selector/isFeatureEnabled';
import { Feature } from 'src/store/app/types';
import { Attribute, Selector } from 'src/store/design/types';
import getStructure from 'src/store/design/selector/getStructure';
import calculateDesignPrice from 'src/util/price/calculateDesignPrice';
import addExtraPrice from 'src/util/price/addExtraPrice';
import formatPrice from 'src/util/price/formatPrice';
import { Price } from 'src/store/price/types';
import { MultiSizeItem } from 'src/store/multisize/types';
import resolveBoundProduct, { getVariantAttributes } from 'src/util/design/resolveBoundProduct';
import {
  findColorSelector, findSizeSelector, getVariantTypeNames, resolveSizeVariant,
} from 'src/util/apparel/sizeVariants';

const NO_EXTRA: Price = { price: 0, priceInitial: 0 };
const MAX_QUANTITY = 99;
// Attribute values are catalogue slugs (e.g. "powder-pink"), so a two-char
// separator is collision-safe for the composite row id.
const ROW_ID_SEPARATOR = '::';

/** One selectable row of the editor-rendered batch selection modal. */
export interface BatchSelectionRowData {
  /** Opaque row id echoed back by the editor — encodes size (and colour). */
  id: string;
  label: string;
  available: boolean;
  minQuantity: number;
  initialQuantity: number;
  maxQuantity: number;
  groupId?: string;
}

export interface BatchSelectionGroupData {
  id: string;
  label: string;
  colorHex?: string;
}

export interface BatchSelectionItem {
  id: string;
  quantity: number;
}

/**
 * Footer state for the modal, fully pre-formatted: the editor renders these
 * strings verbatim so all pricing/formatting logic stays host-side.
 */
export interface BatchSelectionTotals {
  totalText: string;
  priceText: string;
  priceInitialText: string;
  canConfirm: boolean;
}

export const encodeRowId = (sizeValue: string, colorValue?: string): string => (
  colorValue ? `${colorValue}${ROW_ID_SEPARATOR}${sizeValue}` : sizeValue
);

export const decodeRowId = (id: string): { sizeValue: string; colorValue?: string } => {
  const separatorIndex = id.indexOf(ROW_ID_SEPARATOR);
  if (separatorIndex === -1) {
    return { sizeValue: id };
  }
  return {
    colorValue: id.slice(0, separatorIndex),
    sizeValue: id.slice(separatorIndex + ROW_ID_SEPARATOR.length),
  };
};

const withColorOverride = (
  attributes: { [typeName: string]: Attribute },
  colorTypeName: string | undefined,
  colorValue: string | undefined,
): { [typeName: string]: Attribute } => {
  if (!colorTypeName || !colorValue) {
    return attributes;
  }
  return { ...attributes, [colorTypeName]: { typeName: colorTypeName, value: colorValue } };
};

interface VariantContext {
  sizeSelector?: Selector;
  colorSelector?: Selector;
  colorsEnabled: boolean;
  variantTypeNames: string[];
  effectiveAttributes: { [typeName: string]: Attribute };
}

const getVariantContext = (store: Store): VariantContext => {
  const { abstractProduct } = store.design;
  const selectedAttributes = store.design.selectedProductAttributes || {};
  const sizeSelector = findSizeSelector(abstractProduct?.editorSelectors);
  const colorSelector = findColorSelector(abstractProduct?.editorSelectors);
  // Display-only resolution: the fallback to the page selection is acceptable
  // here because the commit path (multiSizeAddToBasketOperation) re-resolves
  // the bound product after the save and refuses to commit without it.
  const boundProduct = resolveBoundProduct(abstractProduct, getStructure(store));
  return {
    sizeSelector,
    colorSelector,
    // Colours are part of the editor drawer itself, gated by the same flag that
    // opens it, so any product exposing a colour selector gets the dimension.
    colorsEnabled: !!colorSelector,
    variantTypeNames: getVariantTypeNames(abstractProduct),
    effectiveAttributes: getVariantAttributes(boundProduct, selectedAttributes),
  };
};

const resolveVariant = (store: Store, context: VariantContext, sizeValue: string, colorValue?: string) => {
  const { abstractProduct } = store.design;
  const attributes = withColorOverride(context.effectiveAttributes, context.colorSelector?.typeName, colorValue);
  return resolveSizeVariant(
    abstractProduct?.products || [],
    context.variantTypeNames,
    context.sizeSelector?.typeName || '',
    sizeValue,
    attributes,
  );
};

/**
 * Rows (and colour groups, when mixed colours are enabled) for the batch
 * selection modal — the store-level counterpart of useMultiSizeRows, for use
 * from operations. A combination with no sibling for the bound variant renders
 * as unavailable; the currently bound colour+size stays at minimum 1.
 */
export const buildBatchSelectionRows = (store: Store): {
  rows: BatchSelectionRowData[];
  groups?: BatchSelectionGroupData[];
  productIds: number[];
} => {
  const context = getVariantContext(store);
  const sizeTypeName = context.sizeSelector?.typeName || '';
  const colorTypeName = context.colorSelector?.typeName || '';
  const sizeOptions = context.sizeSelector?.options || [];
  const currentSizeValue = context.effectiveAttributes[sizeTypeName]?.value;
  const currentColorValue = context.effectiveAttributes[colorTypeName]?.value;

  const colorOptions: (Selector['options'][number] | undefined)[] = context.colorsEnabled
    ? (context.colorSelector?.options || [])
    : [undefined];

  const rows: BatchSelectionRowData[] = [];
  const productIds: number[] = [];

  colorOptions.forEach((colorOption) => {
    const colorValue = colorOption?.value;
    sizeOptions.forEach((sizeOption) => {
      const product = resolveVariant(store, context, sizeOption.value, colorValue);
      if (product) {
        productIds.push(product.productId);
      }
      const isCurrent = sizeOption.value === currentSizeValue && (!context.colorsEnabled || colorValue === currentColorValue);
      const min = isCurrent && product ? 1 : 0;
      rows.push({
        id: encodeRowId(sizeOption.value, colorValue),
        label: sizeOption.name || sizeOption.value,
        available: !!product,
        minQuantity: min,
        initialQuantity: min,
        maxQuantity: MAX_QUANTITY,
        groupId: colorValue,
      });
    });
  });

  const groups = context.colorsEnabled
    ? (context.colorSelector?.options || []).map((option) => ({
      id: option.value,
      label: option.name || option.value,
      colorHex: option.hexColor,
    }))
    : undefined;

  return { rows, groups, productIds };
};

/**
 * Editor row ids → commit items for the multi-size batch endpoint.
 *
 * Quantities are re-validated here rather than trusted: they arrive over
 * postMessage, and a non-integer or out-of-range value would otherwise reach
 * the cart. Ids that resolve to no variant are dropped later, at commit time,
 * where the bound product is known.
 */
export const toMultiSizeItems = (items: BatchSelectionItem[]): MultiSizeItem[] => items
  .filter((item) => Number.isInteger(item.quantity) && item.quantity > 0 && item.quantity <= MAX_QUANTITY)
  .map((item) => ({ ...decodeRowId(item.id), quantity: item.quantity }));

/**
 * Totals for a selection, matching useMultiSizeRows/DesignPrice math: a price
 * tier's p/pi is already the line total for that quantity, then per-unit
 * add-ons (clipart, upsells, bundles) are added × quantity.
 */
export const computeBatchSelectionTotals = (store: Store, items: BatchSelectionItem[]): BatchSelectionTotals => {
  const { abstractProduct } = store.design;
  const categoryId = store.design.categoryId || abstractProduct?.categoryId || 0;
  const context = getVariantContext(store);

  const productTypeId = getProductTypeId(store);
  const totalPages = getTotalPagesCount(store);
  const minPages = getMinPagesCount(store);
  const clipArtsPrice = getUsedClipArtsPrice(store) || NO_EXTRA;
  const upsellPrice = getUpsellTotalPrice(store) || NO_EXTRA;
  const bundledPrice = getProductBundleSelectedProductsTotalPrice(store) || NO_EXTRA;
  const clipArtEnabled = isFeatureEnabled(store, Feature.supportClipArt);
  const currencyId = getCurrencyId(store);
  const priceFormat = getPriceFormat(store);

  let totalUnits = 0;
  let value = 0;
  let initial = 0;
  let known = true;

  items.forEach(({ id, quantity }) => {
    if (quantity <= 0) {
      return;
    }
    const { sizeValue, colorValue } = decodeRowId(id);
    const product = resolveVariant(store, context, sizeValue, colorValue);
    if (!product) {
      return;
    }
    totalUnits += quantity;
    const entry = getPrice(store, categoryId, product.productId, quantity);
    if (!entry || !productTypeId) {
      known = false;
      return;
    }
    let line = calculateDesignPrice(entry, productTypeId, minPages, totalPages);
    if (clipArtEnabled) {
      line = addExtraPrice(line, clipArtsPrice, quantity);
    }
    line = addExtraPrice(line, upsellPrice, quantity);
    line = addExtraPrice(line, bundledPrice, quantity);
    value += line.price;
    initial += line.priceInitial;
  });

  const showPrice = known && value > 0 && !!currencyId && !!priceFormat;

  return {
    totalText: `Total · ${totalUnits} item${totalUnits === 1 ? '' : 's'}`,
    priceText: showPrice ? formatPrice(value, priceFormat || '', currencyId || '') : '—',
    priceInitialText: showPrice && initial > value ? formatPrice(initial, priceFormat || '', currencyId || '') : '',
    canConfirm: totalUnits > 0,
  };
};
